import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import LegalHero from './LegalHero.js';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';
import { subscriptionPlanApi } from '../../services/subscriberApi.js';
import { durationShortLabel, formatPkrAmount, planAvgPerMonth, planSavingsPct } from '../../utils/planPricing.js';

function PlansPricingLine() {
  const [text, setText] = useState('See /plans for current printed subscription prices.');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await subscriptionPlanApi.getActive();
        const list = r?.data?.subscription_plans || r?.data?.plans || [];
        if (!Array.isArray(list) || !list.length) return;
        const parts = list.map((p) => {
          const pct = planSavingsPct(p);
          const avg = planAvgPerMonth(p);
          const save = pct > 0 ? ` (Save ${pct}%, avg ${formatPkrAmount(avg)} / month)` : '';
          return `${durationShortLabel(Number(p.duration_months))}: ${formatPkrAmount(p.price_pkr)}${save}`;
        });
        if (!cancelled) setText(parts.join(' · '));
      } catch {
        /* keep fallback */
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <li><strong>Printed subscriptions:</strong> {text}.</li>
  );
}

const FLARE_RED = '#c4151c';

function PublisherContact() {
  const brand = useSiteBrand();
  return (
    <div className="space-y-1 text-gray-600 leading-relaxed">
      <p><strong>Published by:</strong> Digital Flare Network (Private) Limited</p>
      <p>Editorial &amp; Registered Office: 4th Floor, Media Centre Block, Gulberg III, Lahore 54660, Punjab, Pakistan</p>
      <p>Subscription Helpline: <strong style={{ color: FLARE_RED }}>{brand.phone}</strong> · WhatsApp: <a href={brand.waHref} className="font-semibold hover:underline" style={{ color: FLARE_RED }}>{brand.phone}</a> (Mon–Sat, 10 AM – 6 PM PKT)</p>
      <p>Email: <a href={brand.mailHref} className="font-semibold hover:underline" style={{ color: FLARE_RED }}>{brand.email}</a></p>
    </div>
  );
}

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: (
      <p className="text-gray-600 leading-relaxed">
        By accessing or ordering from <strong>digital.flare.pk</strong> (&ldquo;the Website&rdquo;), owned and operated by <em>Digital Flare Network (Private) Limited</em>, a company incorporated under the Companies Act, 2017 with its registered office in Lahore, Punjab, Pakistan, you confirm that you have read, understood, and agree to be legally bound by these Terms of Service. If you do not agree to any clause, please discontinue use immediately. These terms apply equally to casual browsers, registered subscribers, and wholesale/advertising partners.
      </p>
    ),
  },
  {
    title: '2. User Eligibility',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li>You must be at least <strong>18 years old</strong> to create an account, place a paid order, or sign a subscription contract on this Website.</li>
        <li>Users aged 13–17 may browse only with the supervision and explicit consent of a parent or legal guardian, who accepts full liability for all actions and charges.</li>
        <li>You warrant that all registration, billing, and shipping information you submit is true, accurate, and complete, and that you will update it promptly.</li>
        <li>We reserve the right to refuse service, terminate accounts, or cancel orders in our sole discretion — including for users who violate these Terms or Pakistani law.</li>
      </ul>
    ),
  },
  {
    title: '3. Magazine Ordering &amp; Pricing',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>Flare Magazine is sold in two formats and four subscription durations. Unless clearly marked as &ldquo;Complimentary&rdquo; or &ldquo;Sponsored&rdquo;:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Single printed copy (physical):</strong> Rs 1,500 / issue, inclusive of all applicable taxes (GST 18% where applicable).</li>
          <PlansPricingLine />
          <li><strong>Digital PDF:</strong> Priced per issue as displayed on the issue detail page; non-refundable once downloaded (see Refund Policy).</li>
          <li><strong>Combo (Digital + Print):</strong> Available at a bundled discount of up to 30% vs buying both formats separately.</li>
        </ul>
        <p>All prices are in <strong>Pakistani Rupees (PKR)</strong> and exclude cash-on-delivery (COD) convenience fees and courier delivery charges, which will be shown clearly in your cart <em>before</em> you click &ldquo;Place Order&rdquo;. Prices are subject to change without prior notice, but such changes shall not affect orders that have already been confirmed and paid for.</p>
      </div>
    ),
  },
  {
    title: '4. Subscription Service &amp; Cancellation',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>Subscription terms are as follows:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Start date:</strong> Your subscription term begins with the NEXT issue published after we receive cleared payment, unless you explicitly choose &ldquo;Start from current issue&rdquo; at checkout.</li>
          <li><strong>Duration:</strong> Monthly = 1 issue · Quarterly = 3 issues · Half-Yearly = 6 issues · Yearly = 12 consecutive issues, regardless of calendar months.</li>
          <li><strong>Renewal:</strong> By default, all subscriptions are <strong>manual-renewal</strong> — no auto-debits. We will email + SMS you a 10-day renewal reminder 30 days before the last issue ships. You may opt into auto-renewal at checkout; you can cancel auto-renewal at any time from your Account → Subscriptions page, and we will not charge the next cycle.</li>
          <li><strong>Pause:</strong> You can pause a Quarterly/Half-Yearly/Yearly subscription for up to two (2) issue cycles by emailing subscriptions@digital.flare.pk — remaining issues are simply shifted to later months.</li>
          <li><strong>Cancellation (before first dispatch):</strong> Cancellations received <em>before</em> the first issue is marked &ldquo;Dispatched&rdquo; in your portal will receive a 100% refund minus a 5% admin and payment-processing fee. Cancellations after dispatch are governed by our Refund Policy.</li>
        </ul>
      </div>
    ),
  },
  {
    title: '5. Digital Delivery (PDF Issues)',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>Digital PDF issues are delivered by two methods:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li>Instant download link on the &ldquo;Thank You&rdquo; page immediately after successful payment.</li>
          <li>An email from noreply@digital.flare.pk containing a 72-hour signed download link.</li>
        </ul>
        <p>Links expire after 3 download attempts OR 72 hours to prevent piracy. If you lose access, log in to your Account → Digital Library to re-download unlimited (for personal use only). PDF issues are DRM-free but watermarked with your order number; sharing the file on torrent sites or WhatsApp broadcast lists is a breach of copyright and will result in immediate account termination + legal notice under the Copyright Ordinance, 1962.</p>
      </div>
    ),
  },
  {
    title: '6. Printed Copy Delivery &amp; Disclaimer',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Dispatch timelines:</strong> Lahore city = 1–2 working days · Rest of Punjab = 2–3 working days · Sindh / KPK / Balochistan / AJK / GB = 3–6 working days, after the issue publish date. Delivery partners: TCS Express (default) or Leopard Courier on request.</li>
          <li><strong>Force Majeure:</strong> We are not liable for delayed deliveries caused by courier strikes, natural disasters, Punjab/Islamabad bandhs, political rallies, postal service suspensions, or any other event beyond our reasonable control. In such cases we will extend your subscription by one (1) complimentary issue cycle without extra charge as a goodwill gesture.</li>
          <li><strong>Address correction:</strong> It is YOUR responsibility to confirm the shipping address at checkout. Re-delivery for &ldquo;address not found / recipient shifted&rdquo; returns will be charged at actual courier rates.</li>
          <li><strong>Loss/Damage in transit:</strong> Fully insured if reported via photo/video proof within <strong>24 HOURS</strong> of courier Proof-of-Delivery timestamp. After that window courier denies all claims (see Refund Policy §3).</li>
        </ul>
      </div>
    ),
  },
  {
    title: '7. Returns &amp; Damaged Goods',
    body: (
      <p className="text-gray-600 leading-relaxed">
        Returns and replacement copies are governed exclusively by our <Link to="/refund-policy" style={{ color: FLARE_RED, fontWeight: 600 }}>Refund Policy</Link>, which forms an integral part of these Terms. As a summary: Digital issues — non-returnable, non-refundable once downloaded; Printed issues — replaceable only if damaged, wrong issue shipped, or genuinely lost in transit (7-day window from publish date).
      </p>
    ),
  },
  {
    title: '8. Accepted Payment Methods',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li><strong>Mobile Wallets:</strong> JazzCash, EasyPaisa (merchant QR + invoice links).</li>
        <li><strong>Bank Cards:</strong> Visa, Mastercard, UnionPay debit &amp; credit cards via 1LINK PG (3D Secure mandatory).</li>
        <li><strong>Bank Transfer:</strong> HBL, MCB, UBL, Allied Bank direct IBFT; upload deposit slip on the checkout page for manual verification within 4 working hours.</li>
        <li><strong>Cash on Delivery (COD):</strong> Currently unavailable for single magazine orders. Please pay via JazzCash, EasyPaisa, MCB bank transfer (account title FLARE), or card. Physical singles include courier delivery of Rs 100 for the first copy and Rs 50 for each additional copy, shown at checkout.</li>
      </ul>
    ),
  },
  {
    title: '9. Intellectual Property Rights',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>All content published in Flare Magazine and on digital.flare.pk — including but not limited to editorial articles, investigative features, infographics, illustrations, logo, &ldquo;digital.flare.pk&rdquo; tagline, cover designs, photography, and page layout — is:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li>© 2024–2026 <em>Digital Flare Network (Private) Limited</em>. ALL RIGHTS RESERVED under the Copyright Ordinance, 1962 (Pakistan).</li>
          <li>Registered with the Intellectual Property Organization of Pakistan (IPO-Pakistan), Copyright Office, Islamabad.</li>
          <li>Licensed to the buyer (you) for <strong>personal, non-commercial use only</strong>. Resale, redistribution (WhatsApp groups, Telegram channels, torrents), photocopying in bulk, or inclusion in any paid AI training dataset requires a separate written commercial license from the Publisher.</li>
        </ul>
        <p>Trademarks: &ldquo;Flare Magazine&rdquo;, the flame logo device, &ldquo;digital.flare.pk&rdquo; and all related campaign slogans are registered trademarks of Digital Flare Network (Private) Limited. Unauthorized use on any other website, print publication, or product will be prosecuted under the Trade Marks Ordinance, 2001.</p>
      </div>
    ),
  },
  {
    title: '10. User-Generated Content &amp; Conduct',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>If you submit a letter to the editor, product review, comment, or Contact-page message (&ldquo;User Content&rdquo;), you grant us a perpetual, royalty-free, worldwide, irrevocable license to publish, edit, abridge, translate, or adapt it in print or digital Flare Magazine editions (with attribution to your first name + city, unless you explicitly opt out).</p>
        <p>You agree NOT to:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li>Post any content that is defamatory, religiously offensive, violates the Prevention of Electronic Crimes Act (PECA) 2016, or infringes anyone&rsquo;s privacy.</li>
          <li>Use the Website to distribute malware, phishing links, spam, or bulk marketing messages.</li>
          <li>Attempt to reverse-engineer our checkout system, bypass payment walls, or scrape cover images / editorial content with automated crawlers (robots.txt terms apply).</li>
        </ul>
      </div>
    ),
  },
  {
    title: '11. Limitation of Liability',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <p>To the maximum extent permitted by Pakistani contract law:</p>
        <ul className="space-y-1 list-disc pl-6">
          <li>Our total, aggregate liability to you for any direct damages (breach of contract, non-delivery, faulty goods) shall in NO event exceed the PKR amount you actually paid to us for the specific order/issue from which the claim arose.</li>
          <li>Under NO circumstances shall Flare Magazine or Digital Flare Network be liable for indirect, incidental, consequential, special, or punitive damages — including (without limitation) loss of profit, loss of data, interruption of business, or personal distress arising from the use of or inability to use the Website or magazine content.</li>
          <li>Nothing in these Terms limits liability for our own gross negligence, willful fraud, or death/personal injury caused by our acts.</li>
        </ul>
      </div>
    ),
  },
  {
    title: '12. Dispute Resolution &amp; Governing Law',
    body: (
      <div className="space-y-3 text-gray-600 leading-relaxed">
        <ul className="space-y-1 list-disc pl-6">
          <li><strong>Governing Law:</strong> These Terms and the entire relationship between you and Flare Magazine shall be governed by and construed in accordance with the laws of the <strong>Islamic Republic of Pakistan</strong>, and in particular the Contract Act, 1872; the Sale of Goods Act, 1930; the Electronic Transactions Ordinance, 2002; and the Consumer Protection Act, 2005 (Punjab).</li>
          <li><strong>Jurisdiction:</strong> The courts of <strong>Lahore, Punjab, Pakistan</strong> shall have exclusive jurisdiction over any dispute, controversy, or claim arising out of these Terms.</li>
          <li><strong>Amicable Resolution:</strong> Before filing formal suit, both parties agree to first attempt resolution through direct negotiation (30 days) and, if that fails, binding mediation administered by the <em>Lahore Centre for Alternative Dispute Resolution (LCADR)</em> before any court proceedings.</li>
        </ul>
      </div>
    ),
  },
  {
    title: '13. Miscellaneous',
    body: (
      <ul className="space-y-2 list-disc pl-6 text-gray-600 leading-relaxed">
        <li><strong>Severability:</strong> If any one clause of these Terms is held to be unenforceable or illegal by a competent court, the remaining clauses shall continue in full force.</li>
        <li><strong>Waiver:</strong> No failure or delay by Flare Magazine to exercise any right under these Terms shall constitute a waiver of that right.</li>
        <li><strong>Updates:</strong> We may revise these Terms from time to time; the &ldquo;Last updated&rdquo; date below will be changed accordingly, and active subscribers notified by email.</li>
      </ul>
    ),
  },
  {
    title: '14. Publisher Contact Information',
    body: <PublisherContact />,
  },
];

export default function ShopTermsOfServiceSection() {
  return (
    <ShopLayout pageTitle="Terms of Service" currentPage="terms">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <LegalHero
          kicker="Contract · digital.flare.pk"
          title="Terms of Service"
          updated={<>Last updated: <span className="text-gray-700">January 15, 2026</span> · Governing law: Islamic Republic of Pakistan</>}
          image="/legal/terms.png"
          accent="#4f46e5"
        />

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-10 space-y-8">
          <p className="text-gray-600 leading-relaxed border-l-4 pl-4" style={{ borderColor: '#4f46e5' }}>
            Welcome to <strong>Flare Magazine</strong>. These Terms of Service form a legally binding contract between you (&ldquo;Buyer&rdquo; / &ldquo;Subscriber&rdquo; / &ldquo;User&rdquo;) and <em>Digital Flare Network (Private) Limited</em> (&ldquo;Publisher&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). Please read them carefully before purchasing any issue, starting a subscription, or submitting content.
          </p>

          {SECTIONS.map((s) => (
            <section key={s.title} id={s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')} className="scroll-mt-24">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 pb-2 border-b border-gray-100">
                {s.title}
              </h2>
              {s.body}
            </section>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/plans"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 shadow-[0_8px_20px_-8px_rgba(99,102,241,0.4)]"
            style={{ backgroundColor: '#4f46e5' }}
          >
            Ready to subscribe? Compare our 4 plans →
          </Link>
        </div>
      </div>
    </ShopLayout>
  );
}
